import React, { useState, useEffect, useCallback, useContext, ReactNode, useRef, KeyboardEvent } from "react";
import {
	TextField,
	Paper,
	Grid,
	Button,
	Select,
	MenuItem,
	InputAdornment,
	Tooltip,
	IconButton
} from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import "date-fns";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { UIContext } from "../../../../../contexts";
import STATES from "../../../../../constants/states";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import { DynamicForm, DynamicFormField } from "../../../../../@types/dynamicForm.types";
import { withStyles, Theme } from "@material-ui/core/styles";
import { EditLocationSchema } from "../../../../../@types/group.types";

import EventIcon from "@material-ui/icons/Event";
import SendIcon from "@material-ui/icons/Send";
import InfoIcon from "@material-ui/icons/Info";
import ClearIcon from "@material-ui/icons/Clear";

import "../../../../../globalStyles/paperFormStyles.css";
import "./editLocation.css";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { trackPromise } from "react-promise-tracker";
import { api } from "../../../../../utils/api";
import { Validation } from "../../../../../@types/validation.types";
import { infoTitleMessage } from "../../../../../constants/infoTitleMessage";
import CustomHelperTextComponent from "../../../../shared/customHelperTextComponent/CustomHelperTextComponent";

const LightTooltip = withStyles((theme: Theme) => ({
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		boxShadow: theme.shadows[1],
		maxWidth: 130,
		fontSize: 11
	}
}))(Tooltip);

const mailformat = /^[a-zA-Z0-9.!#$%&'+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)$/;

const EditLocation = (): JSX.Element => {
	const [editLocation, setEditLocation] = useState<DynamicForm>({});
	const [copyLocation, setCopyLocation] = useState<EditLocationSchema>();
	const [locationInformation, setLocationInformation] = useState<EditLocationSchema>({
		group_number: null,
		group_name: "",
		location_number: null,
		location_name: "",
		location_start_date: "",
		email: "",
		phone_number: null,
		address: "",
		city: "",
		state: "",
		ZIP: ""
	});

	const [validation, setValidation] = useState<Validation>({
		locationInformation: {},
		status: "invalid"
	});

	const [pasted, setPasted] = useState(false);

	const _validation = useRef<Validation>();
	const { setDashboardHeader } = useContext(UIContext);
	const queryGroupNumber = useRouteParam("gn");
	const queryLocationNumber = useRouteParam("ln");

	const handleValidation = useCallback(async () => {
		const { location_name, location_start_date, email, phone_number, city, state, ZIP } = locationInformation;

		_validation.current = {
			...validation,
			locationInformation: {},
			status: "invalid"
		};

		let flag = true;

		// validation checking

		if (location_name.length === 0) {
			_validation.current.locationInformation["location_name"] = "Location name is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (location_start_date === "" || location_start_date.length === 0) {
			_validation.current.locationInformation["location_start_date"] = "Location Start Date is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (email.length !== 0) {
			if (!email.match(mailformat)) {
				_validation.current.locationInformation["email"] = "Valid Email is required";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}

		if (phone_number !== null) {
			if (String(phone_number).length !== 10) {
				_validation.current.locationInformation["phone_number"] = "Enter valid 10 digit phone number";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}

		if (city.length === 0) {
			_validation.current.locationInformation["city"] = "City is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (state.length === 0) {
			_validation.current.locationInformation["state"] = "State is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (ZIP.length === 0) {
			_validation.current.locationInformation["ZIP"] = "ZIP Code is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (ZIP.length !== 0) {
			const _zip = ZIP.toString().replaceAll("-", "");
			if (_zip.length !== 5 && _zip.length !== 9) {
				_validation.current.locationInformation["ZIP"] = "Enter 5 or 9 digit ZIP Code!";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}

		if (flag === true) {
			_validation.current["status"] = "valid";
		} else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [locationInformation, validation]);

	// const handleGroupInformationChange = useCallback(
	// 	(event: React.ChangeEvent<HTMLInputElement>) => {
	// 		const { name, value } = event.target;
	// 		setEditGroupInformation(Object.assign({}, editGroupInformation, { [name]: value }));
	// 	},
	// 	[editGroupInformation]
	// );

	// const handleGroupInformationDateChange = useCallback(
	// 	(date: MaterialUiPickersDate, name: string) => {
	// 		const selectedDate = new Date(date as any);
	// 		const parsedDate =
	// 			selectedDate.getFullYear() + "-" + (selectedDate.getMonth() + 1) + "-" + selectedDate.getDate();
	// 		setEditGroupInformation(Object.assign({}, editGroupInformation, { [name]: parsedDate }));
	// 	},
	// 	[editGroupInformation]
	// );

	// const handleContactInformationDateChange = useCallback(
	// 	(date: MaterialUiPickersDate, name: string) => {
	// 		const selectedDate = new Date(date as any);
	// 		const parsedDate =
	// 			selectedDate.getFullYear() + "-" + (selectedDate.getMonth() + 1) + "-" + selectedDate.getDate();
	// 		setEditContactInformation(Object.assign({}, editContactInformation, { [name]: parsedDate }));
	// 	},
	// 	[editContactInformation]
	// );

	// const handleContactInformationChange = useCallback(
	// 	(event: React.ChangeEvent<HTMLInputElement>) => {
	// 		const { name, value } = event.target;
	// 		setEditContactInformation(Object.assign({}, editContactInformation, { [name]: value }));
	// 	},
	// 	[editContactInformation]
	// );

	const handleLocationInformation = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name } = event.target;
			let { value } = event.target;

			if (name === "location_name") {
				const alphaExp = /^[a-zA-Z]+$/;
				if (value.match(alphaExp) || value === "") {
					value = value.slice(0, 1).toUpperCase() + value.slice(1);
					setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
				} else if (/\s/g.test(value)) {
					setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
				}
			} else if (name === "phone_number") {
				if (!isNaN(value as any)) {
					if (value.length <= 10) {
						setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
					} else {
						event.target.value = locationInformation.phone_number
							? (locationInformation.phone_number as unknown as string)
							: "";
						alert("Phone Number cannot be greater 10 digit!");
					}
				} else {
					event.target.value = "";
					event.target.value = locationInformation.phone_number
						? (locationInformation.phone_number as unknown as string)
						: "";
				}
			} else if (name === "ZIP") {
				if (!pasted) {
					const _value = Number(value.replaceAll("-", ""));
					if (!isNaN(_value) && value.length <= 10) {
						const zip = value;
						if (zip.length === 6) {
							value = value.substring(0, 5) + "-" + value.substring(5);
						}

						// console.log("ZIP Event", event.target.value);
						setLocationInformation(
							Object.assign({}, locationInformation, {
								[name]: value
							})
						);
					} else {
						event.target.value = "";
						event.target.value = locationInformation.ZIP;
					}
				} else {
					setPasted(false);
					if (!isNaN(value as any)) {
						if (value.length > 9) {
							event.target.value = "";
							event.target.value = locationInformation.ZIP;
							alert("Please enter a valid 5 or 9 digit ZIP!");
						} else {
							// console.log("Inside Number");
							const zip = value;

							if (zip.length >= 6 && zip.length <= 9) {
								value = value.substring(0, 5) + "-" + value.substring(5);
							}

							setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
						}
					} else {
						// console.log("Inside alpha-numeric");
						let zipValue = value.replace(/[^0-9]/g, "").replace(/[^\w\s]/gi, "");
						//console.log("replaced string", zipValue);
						alert("You have pasted alpha-numeric ZIP!");

						if (zipValue.length > 9) {
							event.target.value = "";
							event.target.value = locationInformation.ZIP;
							alert("Please enter a valid 5 or 9 digit ZIP!");
						} else {
							const zip = zipValue;

							if (zip.length >= 6 && zip.length <= 9) {
								zipValue = zipValue.substring(0, 5) + "-" + zipValue.substring(5);
							}

							setLocationInformation(Object.assign({}, locationInformation, { [name]: zipValue }));
						}
					}
				}
			} else setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));

			// setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
		},
		[locationInformation, pasted]
	);

	const handleLocationInformationDateChange = useCallback(
		(date: MaterialUiPickersDate, name: string) => {
			const selectedDate = new Date(date as any);
			const parsedDate =
				selectedDate.getFullYear() + "-" + (selectedDate.getMonth() + 1) + "-" + selectedDate.getDate();

			setLocationInformation(Object.assign({}, locationInformation, { [name]: parsedDate }));
		},
		[locationInformation]
	);

	const handleHyphenInZip = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "-") {
			event.preventDefault();
		}
	};

	const getParticularLocation = useCallback(async (queryGroupNumber: number, queryLocationNumber: number) => {
		// console.log("groupNumber => ", queryGroupNumber, typeof queryGroupNumber);
		// console.log("locationNumber => ", queryLocationNumber, typeof queryLocationNumber);

		const query = {
			group_number: queryGroupNumber,
			location_number: queryLocationNumber
		};

		const response = await trackPromise(api.location.getParticularLocation(query));
		if (response) {
			// console.log("Fetched Location details => ", response);

			const groupInstance = await trackPromise(api.group.getGroup(queryGroupNumber));
			// console.log("group-information", groupInstance);

			const { location_name, location_start_date, EMAIL, PHONE, ADDR1, CITY, STATE, ZIPCODE } = response;

			setLocationInformation(
				Object.assign({}, locationInformation, {
					group_number: queryGroupNumber,
					group_name: groupInstance.name,
					location_number: queryLocationNumber,
					location_name: location_name,
					location_start_date: location_start_date,
					email: EMAIL,
					phone_number: PHONE,
					address: ADDR1,
					city: CITY,
					state: STATE,
					ZIP: ZIPCODE
				})
			);

			// dummy state
			setCopyLocation(
				Object.assign({}, copyLocation, {
					group_number: queryGroupNumber,
					group_name: groupInstance.name,
					location_number: queryLocationNumber,
					location_name: location_name,
					location_start_date: location_start_date,
					email: EMAIL,
					phone_number: PHONE,
					address: ADDR1,
					city: CITY,
					state: STATE,
					ZIP: ZIPCODE
				})
			);
		}

		// const groupInstance = await trackPromise(api.group.getGroup(queryGroupNumber));
		// console.log("group-information", groupInstance);

		// const {
		// 	group_number,
		// 	location_number,
		// 	location_name,
		// 	location_start_date,
		// 	EMAIL,
		// 	PHONE,
		// 	ADDR1,
		// 	CITY,
		// 	STATE,
		// 	ZIPCODE
		// } = response;

		// setLocationInformation(
		// 	Object.assign({}, locationInformation, {
		// 		group_number: queryGroupNumber,
		// 		group_name: groupInstance.name,
		// 		location_number: queryLocationNumber,
		// 		location_name: location_name,
		// 		location_start_date: location_start_date,
		// 		email: EMAIL,
		// 		phone_number: PHONE,
		// 		address: ADDR1,
		// 		city: CITY,
		// 		state: STATE,
		// 		ZIP: ZIPCODE
		// 	})
		// );

		// // dummy state
		// setCopyLocation(
		// 	Object.assign({}, copyLocation, {
		// 		group_number: queryGroupNumber,
		// 		group_name: groupInstance.name,
		// 		location_number: queryLocationNumber,
		// 		location_name: location_name,
		// 		location_start_date: location_start_date,
		// 		email: EMAIL,
		// 		phone_number: PHONE,
		// 		address: ADDR1,
		// 		city: CITY,
		// 		state: STATE,
		// 		ZIP: ZIPCODE
		// 	})
		// );
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleKeyCheck = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>) => {
			let { value } = event.target as HTMLInputElement;
			const { name } = event.target as HTMLInputElement;
			const eventKey = event.key;

			if (name === "ZIP") {
				if (eventKey === "Backspace" || eventKey === "Delete") {
					if (/[-]/.test(value) && value.length === 7) {
						event.preventDefault();
						const index = value.indexOf("-");
						value = value.substring(0, index);
						setLocationInformation(Object.assign({}, locationInformation, { [name]: value }));
					}
				}
			}
		},
		[locationInformation]
	);

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement | HTMLDivElement>) => {
		const { name } = event.target as HTMLInputElement;
		if (name === "ZIP") {
			setPasted(true);
			setTimeout(() => null, 2000);
		}
	};

	// function will be called when button is clicked
	const handleEditLocationSubmitClick = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validate = await handleValidation();

				if (validate === "valid") {
					const {
						location_number,
						group_number,
						location_name,
						location_start_date,
						email,
						phone_number,
						address,
						city,
						state,
						ZIP
					} = locationInformation;

					const payload = {
						location_number,
						group_number,
						location_name,
						location_start_date,
						EMAIL: email,
						PHONE: phone_number,
						ADDR1: address,
						CITY: city,
						STATE: state,
						ZIPCODE: ZIP
					};

					const response = await trackPromise(api.location.editParticularLocation(payload));

					if (response) {
						alert("Successfully Location has been edited");
						window.location.reload();
					}
				}
			} catch (error) {
				alert("Error! Something went Wrong");
				throw error;
			}
		},
		[handleValidation, locationInformation]
	);
	const handleClearDateField = useCallback(
		(event: any, name: string) => {
			setLocationInformation(Object.assign({}, locationInformation, { [name]: null }));
		},
		[locationInformation]
	);

	// =============================== use Effects =================================

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.edit_location);
	}, [setDashboardHeader]);

	useEffect(() => {
		if (queryGroupNumber && queryLocationNumber) {
			getParticularLocation(queryGroupNumber as number, queryLocationNumber as number);
		}
	}, [getParticularLocation, queryGroupNumber, queryLocationNumber]);

	useEffect(() => {
		setEditLocation(
			Object.assign(
				{},
				{
					"Group Information": [
						{
							label: "Group Number",
							type: "textfield",
							name: "group_number",
							value: locationInformation.group_number,
							placeholder: "Enter group number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleLocationInformation(event)
						},
						{
							label: "Group Name",
							type: "textfield",
							name: "group_name",
							value: locationInformation.group_name,
							placeholder: "Enter group name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleLocationInformation(event)
						},
						{
							label: "Location Number",
							type: "textfield",
							name: "location_number",
							value: locationInformation.location_number,
							placeholder: "Enter location number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleLocationInformation(event)
						},
						{
							label: "Location Name",
							type: "textfield",
							name: "location_name",
							value: locationInformation.location_name,
							placeholder: "Enter location name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleLocationInformation(event)
						},
						{
							label: "Location Start Date",
							type: "date",
							name: "location_start_date",
							value: locationInformation.location_start_date,
							placeholder: "Enter location start date",
							onChange: (date: MaterialUiPickersDate, name: string) =>
								handleLocationInformationDateChange(date, name)
						}
					],
					"Contact Information": [
						{
							label: "Email",
							type: "textfield",
							name: "email",
							value: locationInformation.email,
							placeholder: "Enter email",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleLocationInformation(event)
						},
						{
							label: "Phone Number",
							type: "textfield",
							name: "phone_number",
							value: locationInformation.phone_number,
							placeholder: "Enter phone number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleLocationInformation(event)
						},
						{
							label: "Address",
							type: "textfield",
							name: "address",
							value: locationInformation.address,
							placeholder: "Enter address",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleLocationInformation(event)
						},
						{
							label: "City",
							type: "textfield",
							name: "city",
							value: locationInformation.city,
							placeholder: "Enter city name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleLocationInformation(event)
						},
						{
							label: "State",
							type: "select",
							name: "state",
							value: locationInformation.state,
							placeholder: "Select state name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleLocationInformation(event),
							options: STATES
						},
						{
							label: "ZIP",
							type: "textfield",
							name: "ZIP",
							value: locationInformation.ZIP,
							placeholder: "Enter ZIP",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleLocationInformation(event)
						}
					]
				}
			)
		);
	}, [handleLocationInformationDateChange, handleLocationInformation, locationInformation]);

	useEffect(() => {
		if (locationInformation.location_start_date === null)
			setLocationInformation(Object.assign({}, locationInformation, { location_start_date: "" }));
	}, [locationInformation]);

	return (
		<div className="edit-location-container">
			<div className="pf-action-button-container" id="pf-action-button-container">
				<Button className="button-green" variant="contained" onClick={handleEditLocationSubmitClick}>
					<span className="button-label-with-icon">Submit</span>
					<span>
						<SendIcon className="button-icon" />
					</span>
				</Button>
			</div>
			<Grid container spacing={1} className="pf-grid-container">
				{Object.entries(editLocation).map(([key, value], index) => {
					return (
						<div key={index} className="each-location-form-container">
							<div className="pf-title">{key}</div>
							<Paper className="pf-paper-container" elevation={3} variant="outlined">
								{value.map((eachProp: DynamicFormField, index: number) => {
									const { name, type } = eachProp;
									// console.log("each prop => ", eachProp);

									return (
										<Grid item key={index} xl={12} lg={12} md={12} sm={12} xs={12}>
											<Grid container spacing={1} className="pf-label">
												<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
													{/* Required Field */}
													<div
														className={
															(name === "city" && copyLocation?.city?.length !== 0) ||
															(name === "location_start_date" &&
																copyLocation?.location_start_date?.toString().length !==
																	0) ||
															(name === "state" && copyLocation?.state?.length !== 0) ||
															(name === "ZIP" && copyLocation?.ZIP !== null)
																? "pf-label-text required"
																: "pf-label-text"
														}
													>
														{eachProp.label}
													</div>
												</Grid>

												<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
													{eachProp.type === "textfield" ? (
														<TextField
															className="input-text-field"
															id="first-name-input"
															style={{
																pointerEvents: "unset",
																width: "340px",
																borderRadius: 50
															}}
															name={eachProp.name}
															value={eachProp.value}
															placeholder={eachProp.placeholder}
															disabled={
																name === "group_number" ||
																name === "group_name" ||
																name === "location_number" ||
																name === "location_name"
																	? true
																	: false
															}
															onChange={eachProp.onChange}
															variant="outlined"
															inputProps={{
																onKeyDown:
																	eachProp.name === "ZIP"
																		? handleHyphenInZip
																		: undefined
															}}
															InputProps={{
																endAdornment: (
																	<>
																		{name === "location_number" ||
																		name === "location_name" ||
																		name === "group_number" ||
																		name === "group_name" ? (
																			<InputAdornment position="end">
																				<LightTooltip
																					title={
																						name === "location_number"
																							? infoTitleMessage.location
																							: name === "group_number"
																							? infoTitleMessage.group
																							: name === "group_name"
																							? infoTitleMessage.group
																							: ""
																					}
																					placement="right-start"
																					arrow
																				>
																					<InfoIcon
																						style={{
																							color: "#808080",
																							fontSize: "20px",
																							pointerEvents: "unset"
																						}}
																					/>
																				</LightTooltip>
																			</InputAdornment>
																		) : null}
																	</>
																)
															}}
															helperText={
																validation && validation.locationInformation[name]
																	? validation.locationInformation[name]
																	: null
															}
															onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
																handleKeyCheck(event)
															}
															onPaste={(event: React.ClipboardEvent<HTMLDivElement>) =>
																handlePaste(event)
															}
														/>
													) : eachProp.type === "select" ? (
														<>
															<Select
																id="text-align-options"
																name={eachProp.name}
																onChange={eachProp.onChange}
																value={eachProp.value}
																style={{
																	pointerEvents: "unset",
																	minWidth: 180,
																	textAlign: "left",
																	overflowY: "visible"
																}}
																MenuProps={{
																	style: { zIndex: 35960 }
																}}
																inputProps={{ "aria-label": "Without label" }}
															>
																<MenuItem value="" disabled>
																	Select Value
																</MenuItem>
																{eachProp?.options?.map((option: string) => {
																	return (
																		<MenuItem key={option} value={option}>
																			{option}
																		</MenuItem>
																	);
																})}
															</Select>

															{validation && validation.locationInformation[name] ? (
																<div className="details">
																	<span className="select-validation-text2">
																		{type === "select"
																			? validation.locationInformation[name]
																			: ""}
																	</span>
																</div>
															) : null}
														</>
													) : eachProp.type === "date" ? (
														<MuiPickersUtilsProvider utils={DateFnsUtils}>
															<KeyboardDatePicker
																className="from-date-input"
																id="from-date-input"
																inputVariant="outlined"
																label={eachProp.label}
																placeholder={eachProp.placeholder}
																name={eachProp.name}
																format="MM/dd/yyyy"
																error={false}
																value={eachProp.value}
																onChange={(date: MaterialUiPickersDate) =>
																	eachProp.onChange(date, eachProp.name)
																}
																InputProps={{
																	startAdornment: (
																		<IconButton
																			onClick={(
																				event: React.MouseEvent<
																					HTMLButtonElement,
																					MouseEvent
																				>
																			) =>
																				handleClearDateField(
																					event,
																					eachProp.name
																				)
																			}
																			style={{ order: 1 }}
																		>
																			<ClearIcon
																				color="disabled"
																				fontSize="small"
																			/>
																		</IconButton>
																	),
																	readOnly: true
																}}
																InputLabelProps={{
																	shrink: true
																}}
																keyboardIcon={
																	<EventIcon style={{ color: "#7cb342" }} />
																}
																KeyboardButtonProps={{
																	"aria-label": "change date"
																}}
																style={{
																	pointerEvents: "unset",
																	width: "340px",
																	borderRadius: 50
																}}
																helperText={
																	validation && validation.locationInformation[name]
																		? validation.locationInformation[name]
																		: null
																}
															/>
														</MuiPickersUtilsProvider>
													) : null}
												</Grid>
											</Grid>
										</Grid>
									);
								})}
							</Paper>
						</div>
					);
				})}
			</Grid>
			<div className="pf-action-button-container" id="pf-action-button-container">
				<Button className="button-green" variant="contained" onClick={handleEditLocationSubmitClick}>
					<span className="button-label-with-icon">Submit</span>
					<span>
						<SendIcon className="button-icon" />
					</span>
				</Button>
			</div>
		</div>
	);
};

export default EditLocation;
